import React, { useState, useEffect } from 'react';
import { api } from '../api/client';

const TIER_COLORS = {
  light: { bg: 'rgba(160,174,192,0.15)', border: 'rgba(160,174,192,0.4)', text: '#a0aec0', label: 'Light' },
  medium: { bg: 'rgba(236,201,75,0.15)', border: 'rgba(236,201,75,0.4)', text: '#ecc94b', label: 'Medium' },
  heavy: { bg: 'rgba(128,90,213,0.15)', border: 'rgba(128,90,213,0.4)', text: '#805ad5', label: 'Heavy' },
  ultra: { bg: 'rgba(237,137,54,0.15)', border: 'rgba(237,137,54,0.4)', text: '#ed8936', label: 'Ultra' },
};

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'login_streak', label: 'Login' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'redemptions', label: 'Redemptions' },
  { key: 'points_spent', label: 'Points' },
];

export default function AchievementsPanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('achievements_collapsed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    api.get('/achievements')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const { streak, achievements, days_in_month } = data;
  const filtered = filter === 'all' ? achievements : achievements.filter(a => a.category === filter);
  const completedCount = achievements.filter(a => a.completed).length;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: collapsed ? 0 : 12 }}
        onClick={() => {
          const next = !collapsed;
          setCollapsed(next);
          try { localStorage.setItem('achievements_collapsed', next ? '1' : '0'); } catch {}
        }}
      >
        <h3 style={{ margin: 0 }}>
          <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', marginRight: 8, fontSize: 12 }}>&#9660;</span>
          Monthly Achievements
        </h3>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {completedCount}/{achievements.length} completed
        </span>
      </div>

      {!collapsed && <>
      {/* Streak summary */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 16, padding: '10px 14px',
        background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#ed8936' }}>{streak.current_streak}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Day Streak</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent)' }}>
            {streak.month_login_count}/{days_in_month}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Monthly Logins</div>
        </div>
      </div>

      {/* Category filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, overflowX: 'auto', paddingBottom: 2 }}>
        {CATEGORIES.map(cat => (
          <button
            key={cat.key}
            onClick={() => setFilter(cat.key)}
            style={{
              padding: '5px 12px', fontSize: 12, borderRadius: 20,
              border: filter === cat.key ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: filter === cat.key ? 'var(--accent)' : 'transparent',
              color: filter === cat.key ? '#fff' : 'var(--text-secondary)',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Achievement cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
        {filtered.map(a => {
          const tier = TIER_COLORS[a.tier] || TIER_COLORS.light;
          const req = a.effective_requirement;
          const pct = req > 0 ? Math.min(100, Math.round((a.progress / req) * 100)) : 0;

          return (
            <div key={a.id} style={{
              padding: '12px 14px', borderRadius: 'var(--radius-sm)',
              background: a.completed ? 'rgba(72,187,120,0.1)' : 'var(--bg-secondary)',
              border: a.completed ? '1px solid rgba(72,187,120,0.4)' : '1px solid var(--border)',
              opacity: a.completed ? 0.85 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 20 }}>{a.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>{a.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{a.description}</div>
                  </div>
                </div>
                <span style={{
                  fontSize: 10, padding: '2px 6px', borderRadius: 10,
                  background: tier.bg, color: tier.text, border: `1px solid ${tier.border}`,
                  fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {tier.label}
                </span>
              </div>

              {/* Progress bar */}
              <div style={{
                background: 'var(--bg-primary)', borderRadius: 6, height: 8,
                overflow: 'hidden', marginBottom: 6,
              }}>
                <div style={{
                  width: `${pct}%`, height: '100%', borderRadius: 6,
                  background: a.completed
                    ? 'var(--success)'
                    : `linear-gradient(90deg, ${tier.text}, ${tier.text}aa)`,
                  transition: 'width 0.4s ease',
                }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: a.completed ? 'var(--success)' : 'var(--text-muted)' }}>
                  {a.completed ? 'Completed!' : `${a.progress}/${req}`}
                </span>
                <span style={{ color: 'var(--warning)', fontWeight: 600 }}>
                  +{a.reward_spins} spin{a.reward_spins !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
        Resets monthly on the 1st
      </div>
      </>}
    </div>
  );
}
